import { useEffect, useMemo, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import moment from "moment";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { fetchFleet } from "../../../redux/api/fleet.api";
import { getWorkingRoutesNodes } from "../../../redux/api/expedition.api";

import {
  fetchAssignDriverToNewRoute,
  fetchIntranodesNomenclatures,
  fetchUniqueDrivers,
} from "../../../redux/api/transport.api";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../helpers/apiHelper";

import { dateAndTimeForDatabase } from "../../../utils/dateAndTime";

import ContentCard from "../../../components/misc/ContentCard";
import Dropdown, { DropdownValue } from "../../../components/misc/Dropdown";
import FormButtons from "../../../components/misc/FormButtons";
import DateTimePicker from "../../../components/misc/DateTimePicker";

interface initialValuesInterface {
  sofer_data_start: string | null;
  sofer_data_stop: string | null;
  selectedRoute: DropdownValue | null;
  selectedIntranod: DropdownValue | null;
  selectedStartingNode: DropdownValue | null;
  selectedEndingNode: DropdownValue | null;
  selectedCar: DropdownValue | null;
  selectedReturnCar: DropdownValue | null;
  selectedDriver: DropdownValue | null;
}

const initialValues: initialValuesInterface = {
  sofer_data_start: null,
  sofer_data_stop: null,
  selectedRoute: null,
  selectedIntranod: null,
  selectedStartingNode: null,
  selectedEndingNode: null,
  selectedCar: null,
  selectedReturnCar: null,
  selectedDriver: null,
};

const formikSchema = Yup.object({
  sofer_data_start: Yup.date()
    .nullable()
    .typeError("Data inceput nu este validă")
    .required("Data inceput nu poate fi goală")
    .test(
      "is-greater",
      "Data start trebuie să fie mai mica decat data stop",
      function (value) {
        const { sofer_data_stop } = this.parent;
        return (
          !value ||
          !sofer_data_stop ||
          new Date(value) < new Date(sofer_data_stop)
        );
      },
    ),

  sofer_data_stop: Yup.date()
    .nullable()
    .typeError("Data stop nu este validă")
    .required("Data stop nu poate fi goală")
    .test(
      "is-lesser",
      "Data stop trebuie să fie mai mare decat data start",
      function (value) {
        const { sofer_data_start } = this.parent;
        return (
          !value ||
          !sofer_data_start ||
          new Date(value) > new Date(sofer_data_start)
        );
      },
    ),
  selectedRoute: Yup.object().required("Va rugam selectati ruta").nullable(),

  selectedIntranod: Yup.object()
    .required("Va rugam selectati ruta intranod")
    .nullable(),
  selectedStartingNode: Yup.object()
    .required("Va rugam selectati nodul de start")
    .nullable(),
  selectedEndingNode: Yup.object()
    .required("Va rugam selectati nodul de stop")
    .nullable(),
  selectedCar: Yup.object().required("Va rugam selectati masina").nullable(),
  selectedReturnCar: Yup.object().nullable(),
  selectedDriver: Yup.object().required("Va rugam selectati sofer").nullable(),
});

function AssignDriverComposed() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [formikValues, setFormikValues] =
    useState<initialValuesInterface>(initialValues);
  const [isSaving, setIsSaving] = useState(false);

  const {
    transport: {
      uniqueDrivers: {
        data: { data: uniqueDrivers },
        isLoading: isLoadingUniqueDrivers,
      },
      intranodesNomenclatures: {
        data: { data: intranodesNomenclatures },
        isLoading: isLoadingIntranodesNomenclatures,
      },
      assignedDriverData,
      routesNomenclatures: {
        data: { data: routesNomenclatures },
        isLoading: isLoadingRoutesNomenclatures,
      },
    },
    expedition: {
      workingRoutesNodes: {
        data: workingRoutesNodes,
        isLoading: isLoadingNodes,
      },
    },
    fleet: {
      fleetData: {
        data: { data: fleetData },
        isLoading: isLoadingFleet,
      },
    },
  } = useAppSelector((state) => ({
    transport: state.transport,
    expedition: state.expedition,
    fleet: state.fleet,
  }));

  useEffect(() => {
    dispatch(
      fetchUniqueDrivers({
        perPage: 10000,
      }),
    );
    dispatch(
      fetchIntranodesNomenclatures({
        perPage: 10000,
      }),
    );
    dispatch(
      getWorkingRoutesNodes({
        page: 1,
        perPage: 10000,
      }),
    );
    dispatch(
      fetchFleet({
        perPage: 10000,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    if (assignedDriverData) {
      if (
        uniqueDrivers.length > 0 &&
        intranodesNomenclatures.length > 0 &&
        fleetData.length > 0 &&
        workingRoutesNodes.data.length > 0
      ) {
        const selectedDriver = uniqueDrivers?.find(
          (driver) => driver.id_user === assignedDriverData.id_user,
        );
        const selectedIntranodRoute = intranodesNomenclatures?.find(
          (route) =>
            route.id_ruta_intranod === assignedDriverData.id_ruta_intranod,
        );
        const selectedLocalRoute = routesNomenclatures?.find(
          (route) => route.id_ruta === assignedDriverData.id_ruta,
        );
        const selectedCar = fleetData?.find(
          (car) => car.nrauto === assignedDriverData.nrauto,
        );
        const selectedStartingNode = workingRoutesNodes.data?.find(
          (node) => node.nodNume === assignedDriverData.nod_start,
        );
        const selectedEndingNode = workingRoutesNodes.data?.find(
          (node) => node.nodNume === assignedDriverData.nod_destinatie,
        );

        setFormikValues((prev) => {
          return {
            ...prev,
            selectedIntranod: selectedIntranodRoute
              ? {
                  label: selectedIntranodRoute.denumire,
                  id: selectedIntranodRoute.id_ruta_intranod,
                }
              : null,
            selectedRoute: selectedLocalRoute
              ? {
                  label: selectedLocalRoute.denumire,
                  id: selectedLocalRoute.id_ruta,
                }
              : null,
            selectedStartingNode: selectedStartingNode?.id_nod
              ? {
                  label: selectedStartingNode.nodNume,
                  id: selectedStartingNode.id_nod,
                }
              : null,
            selectedEndingNode: selectedEndingNode?.id_nod
              ? {
                  label: selectedEndingNode.nodNume,
                  id: selectedEndingNode.id_nod,
                }
              : null,
            selectedCar: selectedCar
              ? { label: selectedCar.nrauto, id: selectedCar.id_auto }
              : null,
            selectedDriver: selectedDriver
              ? {
                  label: `${selectedDriver.prenume} ${selectedDriver.nume}`,
                  id: selectedDriver.id_user,
                }
              : null,
          };
        });
      }
    }
  }, [
    assignedDriverData,
    uniqueDrivers,
    routesNomenclatures,
    intranodesNomenclatures,
    fleetData,
    workingRoutesNodes.data,
  ]);

  const handleSave = async (value: initialValuesInterface) => {
    setIsSaving(true);

    const resultAction = await dispatch(
      fetchAssignDriverToNewRoute({
        ...value,
        id_user: value.selectedDriver?.id,
        sofer_data_start: dateAndTimeForDatabase(value.sofer_data_start),
        sofer_data_stop: dateAndTimeForDatabase(value.sofer_data_stop),
        id_ruta_intranod: value.selectedIntranod?.id,
        id_auto: value.selectedCar?.id,
        id_nod_start: value.selectedStartingNode?.id,
        id_nod_stop: value.selectedEndingNode?.id,
        id_ruta: value.selectedRoute?.id,
      }),
    );

    if (
      checkEndpointStatus(resultAction, fetchAssignDriverToNewRoute) !==
      endpointStatus.pending
    ) {
      setIsSaving(false);
    }

    if (
      checkEndpointStatus(resultAction, fetchAssignDriverToNewRoute) ===
      endpointStatus.fulfilled
    ) {
      navigate(-1);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const driversNomenclaturesDropdownValues = useMemo(
    () =>
      uniqueDrivers?.map((item) => ({
        label: `${item.prenume} ${item.nume}`,
        id: item.id_user,
      })),
    [uniqueDrivers],
  );

  const intranodesNomenclaturesDropdownValues = useMemo(
    () =>
      intranodesNomenclatures?.map((item) => ({
        label: item.denumire,
        id: item.id_ruta_intranod,
      })),
    [intranodesNomenclatures],
  );

  const fleetNomenclaturesDropdownValues = useMemo(
    () =>
      fleetData?.map((item) => ({
        label: item.nrauto,
        id: item.id_auto,
      })),
    [fleetData],
  );

  const workingNodesDropdownValues = useMemo(
    () =>
      workingRoutesNodes.data?.map((item) => ({
        label: item.nodNume,
        id: item.id_nod,
      })),
    [workingRoutesNodes.data],
  );

  const routesNomenclaturesDropdownValues = useMemo(
    () =>
      routesNomenclatures?.map((item) => ({
        label: item.denumire,
        id: item.id_ruta,
      })),
    [routesNomenclatures],
  );

  const sortedRoutesNomenclaturesDropdownValues =
    routesNomenclaturesDropdownValues.sort((a, b) => {
      if (a.label.toLowerCase() < b.label.toLowerCase()) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });

  return (
    <ContentCard
      isLoading={
        isLoadingRoutesNomenclatures ||
        isLoadingIntranodesNomenclatures ||
        isLoadingFleet ||
        isLoadingNodes ||
        isLoadingUniqueDrivers
      }
      withShadow={false}
      withPadding={false}>
      <Formik
        enableReinitialize
        initialValues={formikValues}
        validationSchema={formikSchema}
        onSubmit={handleSave}>
        {({
          values,
          isSubmitting,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
        }) => {
          const {
            selectedRoute,
            selectedIntranod,
            selectedCar,
            selectedReturnCar,
            selectedDriver,
          } = values;

          const isStartAfterStop = moment(values.sofer_data_start).isAfter(
            moment(values.sofer_data_stop),
          );

          return (
            <form
              className="form w-100"
              onSubmit={handleSubmit}
              noValidate
              id="kt_login_signin_form">
              <Dropdown
                className="my-4"
                title={"Selecteaza sofer"}
                data={driversNomenclaturesDropdownValues}
                singleValue={selectedDriver}
                setSingleValue={(value) => {
                  setFieldValue("selectedDriver", value);
                }}
                error={errors["selectedDriver"]}
                touched={touched["selectedDriver"]}
              />
              <div className="d-md-flex d-block">
                <DateTimePicker
                  label="Data incepere valabilitate"
                  minDate={new Date()}
                  value={values.sofer_data_start}
                  handleChangeDate={(newDate) => {
                    setFieldValue("sofer_data_start", newDate);
                  }}
                  error={errors["sofer_data_start"]}
                  touched={touched["sofer_data_start"]}
                />

                <DateTimePicker
                  label="Data sfarsit valabilitate"
                  minDate={new Date()}
                  value={values.sofer_data_stop}
                  handleChangeDate={(newDate) => {
                    setFieldValue("sofer_data_stop", newDate);
                  }}
                  error={errors["sofer_data_stop"]}
                  touched={touched["sofer_data_stop"]}
                  withVerticalSpacer
                />
              </div>

              <Dropdown
                className="my-4"
                title={"Selecteaza ruta locala"}
                data={sortedRoutesNomenclaturesDropdownValues}
                singleValue={selectedRoute}
                setSingleValue={(value) => {
                  setFieldValue("selectedRoute", value);
                }}
                error={errors["selectedRoute"]}
                touched={touched["selectedRoute"]}
              />
              <Dropdown
                className="my-4"
                title={"Selecteaza ruta internod"}
                data={intranodesNomenclaturesDropdownValues}
                singleValue={selectedIntranod}
                setSingleValue={(value) => {
                  setFieldValue("selectedIntranod", value);
                }}
                error={errors["selectedIntranod"]}
                touched={touched["selectedIntranod"]}
              />
              <div className="d-flex">
                <Dropdown
                  className="my-4 me-2"
                  title={"Selecteaza nodul de start"}
                  data={workingNodesDropdownValues}
                  singleValue={values.selectedStartingNode}
                  setSingleValue={(value) => {
                    setFieldValue("selectedStartingNode", value);
                  }}
                  error={errors["selectedStartingNode"]}
                  touched={touched["selectedStartingNode"]}
                />
                <Dropdown
                  className="my-4"
                  title={"Selecteaza nodul de stop"}
                  data={workingNodesDropdownValues}
                  singleValue={values.selectedEndingNode}
                  setSingleValue={(value) => {
                    setFieldValue("selectedEndingNode", value);
                  }}
                  error={errors["selectedEndingNode"]}
                  touched={touched["selectedEndingNode"]}
                />
              </div>

              <div className="d-flex">
                <Dropdown
                  className="my-4 me-2"
                  title={"Selecteaza masina"}
                  data={fleetNomenclaturesDropdownValues}
                  singleValue={selectedCar}
                  setSingleValue={(value) => {
                    setFieldValue("selectedCar", value);
                  }}
                  error={errors["selectedCar"]}
                  touched={touched["selectedCar"]}
                />
                <Dropdown
                  className="my-4"
                  title={"Selecteaza masina retur"}
                  data={fleetNomenclaturesDropdownValues}
                  singleValue={selectedReturnCar}
                  setSingleValue={(value) => {
                    setFieldValue("selectedReturnCar", value);
                  }}
                  error={errors["selectedReturnCar"]}
                  touched={touched["selectedReturnCar"]}
                />
              </div>

              <FormButtons
                goBackLabel="Anuleaza"
                goNextLabel="Salveaza"
                handleGoBack={handleCancel}
                nextButtonDisabled={isSubmitting || isStartAfterStop}
                nextButtonLoading={isSaving}
              />
            </form>
          );
        }}
      </Formik>
    </ContentCard>
  );
}

export default AssignDriverComposed;
